import { Component, Input, OnChanges } from '@angular/core';
import { ProMessagingService } from '../pro-messaging.service';
import { MessageModel } from '../../api/models/message-model';
import { NotificationService } from '../../notification/notification.service';
import { AuthService } from '../../core/security/auth.service';
import { FileHelpers } from '../../core/utilities/file.helpers';
import { MediaPreviewModel } from '../../messaging/message/media-preview.model';
import { MatDialog } from '@angular/material/dialog';
import { MatConfirmDialogComponent } from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { FlexModule } from '@angular/flex-layout';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { HighlightPipe } from '../../core/pipes/highlight.pipe';
import { DateTimePipe } from '../../core/pipes/date-time.pipe';

@Component({
    selector: 'app-pro-one-message-v2',
    template: `
    <div
      class="message-item"
      [id]="'msg-' + model.rowNumber"
      [fxLayout]="model.direction === 'out' ? 'row-reverse' : 'row'"
      [ngClass]="{ 'message-item-right': model.direction === 'out', 'message-item-left': model.direction === 'in' }"
    >
      <div class="message-item-body" fxLayout="column inline">
        <!-- Heading -->
        <div class="message-item-heading">
          <div class="message-item-info">
            <span *ngIf="model.direction === 'in'">{{ model.tnFrom | phoneNumber }}</span>
            <span *ngIf="model.direction === 'out'">{{ model.createdBy }}</span>
          </div>
        </div>

        <!-- Content -->
        <div class="message-item-content" fxFlex="auto">
          <span *ngIf="!!model.deletedBy"
          ><i>{{ model.messageText }}</i></span
          >
          <div *ngFor="let media of imagesList">
            <a [href]="media.url" target="_blank">
              <img class="image-preview" [src]="media.url" [alt]="media.displayName" />
            </a>
          </div>
          <div *ngFor="let media of videoList">
            <a [href]="media.url" target="_blank">
              <video controls class="image-preview" [src]="media.url"></video>
            </a>
          </div>
          <div *ngFor="let media of audioList">
            <a [href]="media.url" target="_blank">
              <audio controls class="image-preview" [src]="media.url"></audio>
            </a>
          </div>
          <div *ngFor="let media of otherList">
            <a [href]="media.url" target="_blank" style="display: block">
              {{ media.displayName }}
            </a>
          </div>
          <div style="overflow: hidden;">
            <span
              style="white-space: pre-line; text-align: left; display: inline-block; max-width: 100%;"
              *ngIf="!model.deletedBy"
              [innerHTML]="messageText | highlight: searchText"
            ></span
            ><br />
          </div>
        </div>
        <div class="message-item-footer">
          <span class="status" [ngClass]="stateClass" *ngIf="model.direction === 'out'" [matTooltip]="stateTitle">
            <mat-icon
              color="accent"
              *ngIf="(model.deliveryState === 'waiting' || !model.deliveryState) && this.model.state !== 'error'"
            >done</mat-icon
            >
            <mat-icon color="accent" *ngIf="model.deliveryState === 'delivered'">done</mat-icon>
            <mat-icon color="warn" *ngIf="model.deliveryState === 'not-delivered' || this.model.state === 'error'"
            >clear</mat-icon
            >
          </span>
          <span [title]="model.time | dateTime">{{ model.time | dateTime: 'today' }}</span>
          <button type="button" mat-icon-button [matMenuTriggerFor]="menu" *ngIf="!this.model.broadcastId">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #menu="matMenu">
            <button type="button" mat-menu-item (click)="menuDeleteMessage()" *ngIf="hasDeleteAccess">
              <mat-icon color="warn">delete</mat-icon>
              <span>Delete</span>
            </button>
            <button type="button" mat-menu-item (click)="menuMarkMessageAsUnread()">
              <mat-icon color="primary">mark_chat_unread</mat-icon>
              <span>Mark As Unread</span>
            </button>
          </mat-menu>
        </div>
      </div>
    </div>
  `,
    styleUrls: ['./pro-one-message-v2.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        NgClass,
        NgIf,
        NgFor,
        MatTooltip,
        MatIcon,
        MatIconButton,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        PhoneNumberPipe,
        HighlightPipe,
        DateTimePipe,
    ],
})
export class ProOneMessageV2Component implements OnChanges {
  @Input() model: MessageModel;
  @Input() searchText: string;

  stateClass: string;
  stateTitle: string;
  hasDeleteAccess = false;
  messageText: string;
  imagesList: MediaPreviewModel[] = [];
  audioList: MediaPreviewModel[] = [];
  videoList: MediaPreviewModel[] = [];
  otherList: MediaPreviewModel[] = [];

  constructor(
    public messagingService: ProMessagingService,
    private notificationService: NotificationService,
    public authService: AuthService,
    public dialog: MatDialog,
  ) {}

  ngOnChanges() {
    this.hasDeleteAccess = this.authService.permissions.hasProMessagingDeleteAccess && !this.model.broadcastId;

    this.messageText = this.model.messageText;

    this.stateClass = 'status-sent';
    this.stateTitle = 'status-sent';

    if (this.model.deliveryState === 'waiting' && this.model.state !== 'error') {
      this.stateClass = 'status-sent';
      this.stateTitle = this.model.state === 'queued' ? 'Message Queued' : 'Message Sent';
    } else if (this.model.deliveryState === 'delivered') {
      this.stateClass = 'status-delivered';
      this.stateTitle = 'Message Delivered';
    } else if (this.model.deliveryState === 'not-delivered') {
      this.stateClass = 'status-failed';
      this.stateTitle = `Message Failed: ${this.model.deliveryDescription}`;
    } else if (this.model.state === 'error') {
      this.stateClass = 'status-failed';
      this.stateTitle = `Message Failed: ${this.model.deliveryDescription}`;
    }

    this.imagesList = [];
    this.audioList = [];
    this.videoList = [];
    this.otherList = [];

    if (this.model.media) {
      let index = 1;
      for (const media of this.model.media) {
        const mediaFile = new MediaPreviewModel();
        mediaFile.filename = media.filename;
        mediaFile.displayName = `attachment-${index++}.${FileHelpers.getExtension(media.filename)}`;
        const mimetypeByFileName = FileHelpers.getMimeType(media.filename);

        if (media.mediaType.indexOf('image') > -1 || mimetypeByFileName.indexOf('image') > -1) {
          mediaFile.url = `/api/advanced-messaging/stream-media/${media.filename}`;
          this.imagesList.push(mediaFile);
        } else if (media.mediaType.indexOf('audio') > -1 || mimetypeByFileName.indexOf('audio') > -1) {
          mediaFile.url = `/api/advanced-messaging/stream-media/${media.filename}`;
          this.audioList.push(mediaFile);
        } else if (media.mediaType.indexOf('video') > -1 || mimetypeByFileName.indexOf('video') > -1) {
          mediaFile.url = `/api/advanced-messaging/stream-media/${media.filename}`;
          this.videoList.push(mediaFile);
        } else {
          mediaFile.url = `/api/advanced-messaging/download-media/${media.filename}`;
          this.otherList.push(mediaFile);
        }
      }
    }
  }

  menuDeleteMessage() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      data: {
        title: 'Delete Message',
        text: 'Are you sure you want to delete message?',
        actionTitle: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.onDeleteProcess();
      }
    });
  }

  async onDeleteProcess(): Promise<void> {
    try {
      this.model = await this.messagingService.deleteMessageBodyById(this.model.messageId);
      await this.messagingService.markUnreadMessagesByThreadKey(this.model.threadKey);
      this.ngOnChanges();
      this.notificationService.notifySuccess('The message was successfully deleted');
    } catch (e) {
      console.error(e);
    }
  }

  async menuMarkMessageAsUnread() {
    await this.messagingService.markMessageAsUnreadByThreadKey(this.model.threadKey);
  }
}
